/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { transformIntoArray } from 'modules/common/utils/array';
import { DEMO_ROLE, USER_ROLE } from 'modules/common/constants/roles';
import { UiController } from '..';
import { EXECUTION_TYPE, consoleCommands, decodedTabs } from './constants';

const KNOWN_COMMANDS = new Set(Object.values(consoleCommands));
/**
 * Warn about command keys that no handler recognises, so broken share urls are not silent
 * @param {String} command - command key, ex: sd
 */
export const reportUnknownCommand = (command) => {
  if (command && !KNOWN_COMMANDS.has(command)) {
    console.warn(`[ui-controller] unknown command "${command}" ignored`);
  }
};
/**
 * Get data value based on execution type that get called form different places
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 * @returns
 */
export const setDataAndSuccessResponse = (commandData, type) => {
  let data = '';
  switch (type) {
    case EXECUTION_TYPE.COMMAND:
      data = commandData.value;
      break;
    case EXECUTION_TYPE.CONVERSATION:
      data = commandData;
      break;
    case EXECUTION_TYPE.URL:
      data = commandData.substring(1);
      break;
    default:
      break;
  }
  //
  return data;
};
// whether a command url has at least one known command key
const hasKnownCommand = (commandUrl) =>
  commandUrl?.split('&').some((command) => KNOWN_COMMANDS.has(command.split('=')[0])) ?? false;
/**
 * Get the plain search string of a share url.
 * Share urls copied before the url encryption option was removed can be base64 encoded,
 * those are decoded so that they still apply. A plain search string is returned as it is.
 * @param {String} search - search string of the location, ex: ?sty=lw&seg=rc0
 * @returns {String} plain search string
 */
export const decodeLegacyCommandUrl = (search) => {
  const commandUrl = search?.startsWith('?') ? search.substring(1) : search;
  if (!commandUrl || hasKnownCommand(commandUrl)) return search;
  try {
    const decoded = atob(decodeURIComponent(commandUrl));
    return hasKnownCommand(decoded) ? `?${decoded}` : search;
  } catch (error) {
    // not an encoded url
    return search;
  }
};
/**
 * Split a command url into its search (commands) and hash (tab) parts
 * @param {String} url - ex: sd=lw&htl=Nomo Soho#pace
 * @returns {{search: String, hash: String}}
 */
export const splitCommandUrl = (url) => {
  const [search, hash] = url?.split('#') ?? [];
  return { search, hash };
};
/**
 * Check whether a VI result was generated from a share url other than the current one
 * @param {String} resultUrl - share url stored with the VI result, ex: sd=lw&htl=Nomo Soho#pace
 * @param {String} currentUrl - current share url commands, ex: sd=lw&htl=Nomo Soho
 * @param {String} hash - hash of the current tab, ex: pace
 * @returns {Boolean}
 */
export const isHistoricalViResult = (resultUrl, currentUrl, hash) =>
  Boolean(resultUrl) && resultUrl !== `${currentUrl}#${hash}`;
/**
 * Get the tab command value for a url hash
 * @param {String} hash - ex: pace
 * @returns {String|undefined} ex: pa
 */
export const resolveTabFromHash = (hash) => decodedTabs[hash];
/**
 * Check whether the given tab can be opened by the user role
 * @param {String} tab - tab command value
 * @param {String} userRole
 * @returns {Boolean}
 */
export const isTabAllowedForRole = (tab, userRole) =>
  !(tab === decodedTabs['i-prefer'] && (userRole === USER_ROLE || userRole === DEMO_ROLE));
/**
 * Set hotel of dashboard view from the value retrieved from the command url
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 * @returns {String|null} command key that was emitted (hotel or hotel group), null if none
 */
export const setHotelCommand = (commandData, type) => {
  const data = setDataAndSuccessResponse(commandData, type);
  // breaks down command url
  const commands = data?.split('&');
  // checked whether hotel command exists or not in command url
  const hotel = commands?.filter((com) => com.includes(consoleCommands.HOTEL));
  const hotelGroup = commands?.filter((com) => com.includes(consoleCommands.HOTEL_GROUP));
  // if there is hotel command emit event to set hotel
  if (hotel?.length > 0) {
    const values = transformIntoArray(hotel[0]?.split('=')[1]);
    UiController.setHotelName(values);
    return consoleCommands.HOTEL;
  }
  if (hotelGroup?.length > 0) {
    const groupValues = transformIntoArray(hotelGroup[0]?.split('=')[1]);
    UiController.setHotelGroupName(groupValues);
    return consoleCommands.HOTEL_GROUP;
  }
  return null;
};
// value parsers used by the command table
const asString = (value) => value;
const asInt = (value) => parseInt(value, 10);
const asList = (value) => transformIntoArray(value);
/**
 * Single source of truth for the ui controller commands.
 * Each entry maps a command key to how its value is parsed and which UiController setter receives it.
 * singleOnly - executed by executeSingleCommands only; in a multiple command url they are applied
 * beforehand from the url hash / setHotelCommand
 */
const COMMAND_TABLE = {
  [consoleCommands.STAY_DATE]: { parse: asString, emit: (v) => UiController.setDateRange(v) },
  [consoleCommands.HOTEL]: {
    parse: asList,
    emit: (v) => UiController.setHotelName(v),
    singleOnly: true,
  },
  [consoleCommands.HOTEL_GROUP]: {
    parse: asList,
    emit: (v) => UiController.setHotelGroupName(v),
    singleOnly: true,
  },
  [consoleCommands.PMS]: { parse: asInt, emit: (v) => UiController.setPmsSync(v) },
  [consoleCommands.TAB]: { parse: asString, emit: (v) => UiController.setTab(v), singleOnly: true },
  [consoleCommands.BOOKING_DATE]: { parse: asString, emit: (v) => UiController.setBookingDate(v) },
  [consoleCommands.WEEKLY_VIEW]: { parse: asInt, emit: (v) => UiController.setWeeklyView(v) },
  [consoleCommands.ALIGNMENT]: { parse: asInt, emit: (v) => UiController.setAlignment(v) },
  [consoleCommands.SYMMETRIC]: {
    parse: asInt,
    emit: (v) => UiController.setSymmetricComparison(v),
  },
  [consoleCommands.CUMULATIVE_VIEW]: {
    parse: asInt,
    emit: (v) => UiController.setCumulativeView(v),
  },
  [consoleCommands.COMPARE_WINDOW]: {
    parse: asString,
    emit: (v) => UiController.setCompareWIndow(v),
  },
  [consoleCommands.PACE_HORIZON]: { parse: asString, emit: (v) => UiController.setPaceHorizon(v) },
  [consoleCommands.PACE_VARIABLE]: {
    parse: asString,
    emit: (v) => UiController.setPaceVariable(v),
  },
  [consoleCommands.SEGMENT]: { parse: asString, emit: (v) => UiController.setBreakdown(v) },
  [consoleCommands.COMPARE_HOTEL]: { parse: asList, emit: (v) => UiController.setCompareHotels(v) },
  [consoleCommands.FOCUS]: { parse: asList, emit: (v) => UiController.setFocusOn(v) },
  [consoleCommands.STAY_BACKWARD_PERIOD]: {
    parse: asInt,
    emit: (v) => UiController.setStayBackwardPeriod(v),
  },
  [consoleCommands.STAY_BACKWARD_MONTH]: {
    parse: asInt,
    emit: (v) => UiController.setStayBackwardMonth(v),
  },
  [consoleCommands.STAY_FORWARD_PERIOD]: {
    parse: asInt,
    emit: (v) => UiController.setStayForwardPeriod(v),
  },
  [consoleCommands.STAY_FORWARD_MONTH]: {
    parse: asInt,
    emit: (v) => UiController.setStayForwardMonth(v),
  },
  [consoleCommands.NORMALIZED_VIEW]: {
    parse: asInt,
    emit: (v) => UiController.setNormalizedView(v),
  },
  [consoleCommands.WEB_CLICK_STEPS]: { parse: asList, emit: (v) => UiController.setSteps(v) },
  [consoleCommands.WEB_CLICK_SCALE]: { parse: asInt, emit: (v) => UiController.setScaleFactor(v) },
  [consoleCommands.CUSTOM_DATES]: {
    parse: asString,
    emit: (v) => UiController.setCustomDateRangeValue(v),
  },
  [consoleCommands.SET_A]: { parse: asList, emit: (v) => UiController.setCustomAggregationA(v) },
  [consoleCommands.SET_B]: { parse: asList, emit: (v) => UiController.setCustomAggregationB(v) },
  [consoleCommands.TABULAR_VIEW]: { parse: asString, emit: (v) => UiController.setTabularView(v) },
  [consoleCommands.METRIC_SELECTION]: {
    parse: asString,
    emit: (v) => UiController.setMetricSelection(v),
  },
  [consoleCommands.TIME_FRAME]: { parse: asString, emit: (v) => UiController.setTimeFrame(v) },
  [consoleCommands.LAG_ALIGN]: { parse: asInt, emit: (v) => UiController.setLagAligned(v) },
  [consoleCommands.PERCENTAGE]: { parse: asString, emit: (v) => UiController.setPercentage(v) },
  [consoleCommands.BOOKING_FORECAST]: {
    parse: asList,
    emit: (v) => UiController.setBookingForecast(v),
  },
  [consoleCommands.EXTENDING_FORECAST]: {
    parse: asInt,
    emit: (v) => UiController.setExtendingForecast(v),
  },
  [consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE]: {
    parse: asInt,
    emit: (v) => UiController.setSymmetricComparisonCollapse(v),
  },
  [consoleCommands.VISUAL_LOADING]: {
    parse: asString,
    emit: (v) => UiController.setVisualCollapse(v),
  },
  [consoleCommands.FORECAST]: { parse: asInt, emit: (v) => UiController.setForecastToggle(v) },
  [consoleCommands.COMPARE_HOTEL_GROUP]: {
    parse: asList,
    emit: (v) => UiController.setSecondaryHotelGroup(v),
  },
  [consoleCommands.ACTIVE_TREND_TAB]: {
    parse: asString,
    emit: (v) => UiController.setActiveTab(v),
  },
  [consoleCommands.BREAKDOWN_TOGGLE]: {
    parse: asInt,
    emit: (v) => UiController.setBreakdownToggle(v),
  },
  [consoleCommands.PERCENTAGE_TOGGLE]: {
    parse: asInt,
    emit: (v) => UiController.setPercentageChangeToggle(v),
  },
  [consoleCommands.SEGMENT_BREAKDOWN]: {
    parse: asInt,
    emit: (v) => UiController.setSegmentBreakdownToggle(v),
  },
  [consoleCommands.UTM_BREAKDOWN]: {
    parse: asString,
    emit: (v) => UiController.setUTMBreakdown(v),
  },
  [consoleCommands.UTM_FOCUS_ON]: { parse: asList, emit: (v) => UiController.setUTMFocusOn(v) },
  [consoleCommands.BOOKING_PACE_WINDOW]: {
    parse: asString,
    emit: (v) => UiController.setBookingPaceWindow(v),
  },
  [consoleCommands.MOVING_AVERAGE]: {
    parse: asString,
    emit: (v) => UiController.setMovingAverage(v),
  },
  [consoleCommands.CURRENCY]: { parse: asString, emit: (v) => UiController.setCurrency(v) },
  [consoleCommands.DATE_TYPE]: { parse: asString, emit: (v) => UiController.setDateType(v) },
  [consoleCommands.RATE_PLAN]: { parse: asInt, emit: (v) => UiController.setRatePlan(v) },
  [consoleCommands.IATA]: { parse: asString, emit: (v) => UiController.setIATA(v) },
  [consoleCommands.RRV]: { parse: asInt, emit: (v) => UiController.setRibbonRevenueView(v) },
  [consoleCommands.ROV]: { parse: asInt, emit: (v) => UiController.setRibbonOccupiedRoomsView(v) },
  [consoleCommands.RAV]: { parse: asInt, emit: (v) => UiController.setRibbonAdrView(v) },
  [consoleCommands.ROOM_NIGHTS_PACE]: {
    parse: asInt,
    emit: (v) => UiController.setRoomNightsPaceView(v),
  },
  [consoleCommands.GRANULARITY]: { parse: asString, emit: (v) => UiController.setGranularity(v) },
  [consoleCommands.VI]: { parse: asString, emit: (v) => UiController.setVIResponse(v) },
  [consoleCommands.ROOM_TYPE]: { parse: asInt, emit: (v) => UiController.setRoomType(v) },
};
/**
 * Execute one key=value command through the command table
 * @param {String} command - ex: sd=lw
 * @param {Boolean} allowSingleOnly - whether singleOnly commands are executed
 */
const executeCommand = (command, allowSingleOnly) => {
  const [key, value] = command?.split('=') ?? [];
  const entry = Object.hasOwn(COMMAND_TABLE, key) ? COMMAND_TABLE[key] : undefined;
  if (!entry) {
    reportUnknownCommand(key);
    return;
  }
  if (entry.singleOnly && !allowSingleOnly) {
    return;
  }
  entry.emit(entry.parse(value));
};
/**
 * Get the command keys of a command url
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 * @returns {Array} ex: ['sty', 'seg', 'fcs']
 */
export const getCommandKeys = (commandData, type) => {
  const data = setDataAndSuccessResponse(commandData, type);
  return (data?.split('&') ?? []).map((command) => command.split('=')[0]).filter(Boolean);
};
/**
 * Execute single commands
 * This where ui controller single commands are executed.
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 * @returns
 */
export const executeSingleCommands = (commandData, type) => {
  executeCommand(setDataAndSuccessResponse(commandData, type), true);
};
/**
 * Execute multiple commands
 * This where ui controller multiple commands are executed.
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 * @returns
 */
export const executeMultipleCommands = (commandData, type) => {
  const data = setDataAndSuccessResponse(commandData, type);
  // breaks down command url and execute each command
  data?.split('&').forEach((command) => executeCommand(command, false));
};
